import React from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from 'lucide-react';
import type { Processo } from '../servicos/servicoGerenciadorProcessos';
import IconButton from '../../../core/componentes/comuns/IconButton';
import { Tooltip } from '../../../core/componentes/Tooltip';
import { useModal } from '../../../core/contextos/modalContext';
import { obterLabelTipoVertical, obterCoresTipoVertical, obterRotaPorTipoVertical } from '../tipos/tiposVerticais';

/**
 * ListaProcessos
 *
 * Componente que exibe uma lista de processos em formato de linhas, com barra de progresso, status, etapa e ações rápidas.
 *
 * Props:
 * - processos: lista de processos a exibir
 * - onArquivar: função chamada ao arquivar um processo
 * - onExcluir: função chamada ao excluir um processo
 */
export function ListaProcessos({ processos, onExcluir, onRenomear }: {
  processos: Processo[];
  onExcluir?: (id: string) => void;
  onRenomear?: (id: string, novoNome: string) => void;
}): JSX.Element {
  const navigate = useNavigate();
  
  // Hook para controlar modais de alerta/confirmação
  const { mostrarAlerta } = useModal();

  return (
  <div className="lista-processos box-border w-full max-w-full overflow-x-hidden">
      <div className="hidden grid-cols-13 gap-2 rounded-t-lg border-b bg-gray-50 px-2 py-1 font-semibold text-gray-700 md:grid" style={{ gridTemplateColumns: 'repeat(13, minmax(0, 1fr))' }}>
        <div className="col-span-2">Nome</div>
        <div className="col-span-2">Vertical</div>
        <div className="col-span-2">Criador</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-2">Progresso</div>
        <div className="col-span-1">Etapa</div>
        <div className="col-span-2 text-right">Ações</div>
      </div>
      <ul className="max-w-full divide-y divide-gray-200">
        {processos.map((processo) => {
          const aguardandoUsuario = !!(processo.dados?.aguardando_usuario);
          return (
            <li key={processo.id} className="box-border grid w-full grid-cols-13 items-center gap-2 p-2 transition-colors hover:bg-gray-50" style={{ gridTemplateColumns: 'repeat(13, minmax(0, 1fr))' }}>
              <div className="col-span-2 truncate" title={processo.nome}>{processo.nome}</div>
              {/* TAREFA-224: Coluna de Vertical com badge */}
              <div className="col-span-2">
                <span className={`inline-block rounded-full px-2 py-1 text-xs font-medium ${obterCoresTipoVertical(processo.tipoVertical)}`}>
                  {obterLabelTipoVertical(processo.tipoVertical)}
                </span>
              </div>
              {/* Coluna do Criador */}
              <div className="col-span-2">
                {processo.ehMeu === false && processo.nomeUsuario ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 text-xs text-purple-700" title={`Criado por ${processo.nomeUsuario}`}>
                    <User className="h-3 w-3" />
                    {processo.nomeUsuario}
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">—</span>
                )}
              </div>
              <div className="col-span-2">
                <span className={`inline-block rounded px-2 py-1 text-xs font-medium ${processo.status === 'concluido' ? 'bg-green-100 text-green-800' : processo.status === 'em_andamento' ? 'bg-blue-100 text-blue-800' : processo.status === 'pendente' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>{processo.status}</span>
                {aguardandoUsuario && <span className="ml-2 text-xs text-orange-700" aria-label="Aguardando ação do usuário">Aguardando</span>}
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <div className="h-2 w-full rounded bg-gray-200" aria-label="Barra de progresso">
                  <div className={`h-2 rounded ${processo.status === 'concluido' ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${processo.progresso}%`, transition: 'width 550ms cubic-bezier(.22,.9,.2,1)' }}></div>
                </div>
                <span className="ml-2 text-xs text-gray-600">{processo.progresso}%</span>
              </div>
              <div className="col-span-1 text-center">{(processo.etapaAtual || 0) + 1}/6</div>
              <div className="col-span-2 flex justify-end gap-2">
                <Tooltip text="Continuar">
                  <IconButton aria-label="Continuar" srLabel="Continuar processo" onClick={() => {
                    // TAREFA-224: Rotear para a página correta baseado no tipo de vertical
                    const rota = obterRotaPorTipoVertical(processo.tipoVertical);
                    navigate(`${rota}?processo_id=${processo.id}`);
                  }}>
                  {/* Right arrow / continue icon */}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                    <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  </IconButton>
                </Tooltip>



                <Tooltip text="Renomear">
                  <IconButton aria-label="Renomear" srLabel="Renomear processo" onClick={() => {
                  // TODO: Implementar modal de input customizado (TAREFA futura)
                  // Por enquanto, usando prompt nativo pois requer input do usuário
                  const novo = window.prompt('Novo nome do processo:', processo.nome || '');
                  if (novo === null) return;
                  const nomeTrim = (novo || '').trim();
                  if (!nomeTrim) { 
                    mostrarAlerta({
                      titulo: 'Nome Inválido',
                      mensagem: 'O nome do processo não pode ficar vazio.',
                    }); 
                    return; 
                  }
                  if (onRenomear) onRenomear(processo.id, nomeTrim);
                }}>
                  {/* Pencil icon */}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                    <path d="M3 21v-3.75L14.81 5.44a2 2 0 0 1 2.82 0l1.94 1.94a2 2 0 0 1 0 2.82L7.75 21H3z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  </IconButton>
                </Tooltip>

                <Tooltip text="Excluir">
                  <IconButton aria-label="Excluir" srLabel="Excluir processo" onClick={() => onExcluir && onExcluir(processo.id)}>
                  {/* Trash icon */}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                    <path d="M3 6h18" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 6v14a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  </IconButton>
                </Tooltip>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
